import { jsxs, jsx } from "react/jsx-runtime";
import { Box, TooltipPopper, Tooltip, Paragraph, Anchor } from "@zillow/constellation";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import { TextInline, usePageProps, getCmsInfoForPage } from "@zillow/zrs-cms";
import * as HtmlToReact from "html-to-react";
import { omit } from "lodash";
import React from "react";
import { withTrigger } from "./helpers.esm.js";
import { usePopulateParamPlaceHolders } from "./populateParamPlaceholders.esm.js";
const HtmlToReactParser = HtmlToReact.Parser;
const AnchorWithTrigger = withTrigger(Anchor);
const htmlToReactParser = new HtmlToReactParser();
const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);
function findDirectParent(node, predicate) {
  const parent = node.parent;
  return parent && predicate(parent) ? parent : null;
}
function findDirectChild(node, predicate) {
  if (!node.children) return null;
  for (const child of node.children) {
    if (predicate(child)) {
      return child;
    }
  }
  return null;
}
function processingInstructions({
  _blockAttributes
}) {
  return [
    // Spans
    {
      shouldProcessNode(node) {
        var _a;
        return node.name === "span" && ((_a = node.attribs) == null ? void 0 : _a.color);
      },
      processNode(node, children, nodeIndex) {
        const ProcessedSpan = () => {
          return /* @__PURE__ */ jsx(TextInline, { fontColor: node.attribs.color, children }, `${nodeIndex}-span-${node.name}`);
        };
        return /* @__PURE__ */ jsxs(ProcessedSpan, { children: [
          " ",
          children
        ] });
      }
    },
    // Links
    {
      shouldProcessNode(node) {
        return node.name === "a";
      },
      processNode(node, children, nodeIndex) {
        const CustomAnchor = () => {
          var _a, _b;
          const pageProps = usePageProps();
          const href = usePopulateParamPlaceHolders(node.attribs.href);
          const parentColorSpan = findDirectParent(node, (parent) => {
            var _a2;
            return parent.name === "span" && ((_a2 = parent.attribs) == null ? void 0 : _a2.color);
          });
          const childColorSpan = findDirectChild(node, (child) => {
            var _a2;
            return child.name === "span" && ((_a2 = child.attribs) == null ? void 0 : _a2.color);
          });
          const inheritedColor = ((_a = childColorSpan == null ? void 0 : childColorSpan.attribs) == null ? void 0 : _a.color) || ((_b = parentColorSpan == null ? void 0 : parentColorSpan.attribs) == null ? void 0 : _b.color);
          const anchorProps = {
            triggerLink: node.attribs.href,
            key: `${nodeIndex}-anchor-${node.name}`,
            ...omit(node.attribs, "style", "class"),
            href,
            sx: {
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              ...inheritedColor && {
                "&&&": {
                  color: `$colors.${inheritedColor}`,
                  ":visited": {
                    color: `$colors.${inheritedColor}`
                  },
                  ":hover": {
                    color: `$colors.${inheritedColor}`
                  },
                  ":not(:active):hover:not(:disabled)": {
                    color: `$colors.${inheritedColor}`
                  }
                }
              }
            }
          };
          return /* @__PURE__ */ jsx(AnchorWithTrigger, { ...anchorProps, onMouseEnter: (e) => {
            event({
              // https://dataportal.zillowgroup.net/event-registry/2049
              envelope: {
                event_type_id: "2049",
                event_type_version_id: "1",
                event_template_id: "126",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_anchor",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_hover"
              },
              cms_info: {
                block_id: e.target.id || "",
                block_type_cd: "anchor",
                block_displayed_txt: e.target.textContent,
                block_target_url: href,
                block_target_window_txt: node.attribs.target || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }, onClick: yieldCallback((e) => {
            var _a2, _b2;
            (_b2 = (_a2 = node.attribs).onClick) == null ? void 0 : _b2.call(_a2, e);
            event({
              // https://dataportal.zillowgroup.net/event-registry/2048
              envelope: {
                event_type_id: "2048",
                event_type_version_id: "1",
                event_template_id: "126",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_anchor",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_click"
              },
              cms_info: {
                block_id: e.target.id || "",
                block_type_cd: "anchor",
                block_displayed_txt: e.target.textContent,
                block_target_url: node.attribs.href,
                block_target_window_txt: node.attribs.target || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }), children });
        };
        return /* @__PURE__ */ jsx(CustomAnchor, {}, `${nodeIndex}-anchor-container-${node.name}`);
      }
    },
    // Tooltips
    {
      shouldProcessNode(node) {
        var _a;
        return ((_a = node.attribs) == null ? void 0 : _a.class) === "constellation-tooltip";
      },
      processNode(node, children, nodeIndex) {
        const TooltipContainer = () => {
          const pageProps = usePageProps();
          return /* @__PURE__ */ jsx(TooltipPopper, { onOpen: () => {
            var _a;
            event({
              // https://dataportal.zillowgroup.net/event-registry/5030
              envelope: {
                event_type_id: "5030",
                event_type_version_id: "1",
                event_template_id: "297",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_tooltip",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_open"
              },
              cms_info: {
                block_id: ((_a = node == null ? void 0 : node.attribs) == null ? void 0 : _a.id) || "",
                block_type_cd: "tooltip",
                block_displayed_txt: children[0] || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }, onClose: () => {
            var _a;
            event({
              // https://dataportal.zillowgroup.net/event-registry/2051
              envelope: {
                event_type_id: "2051",
                event_type_version_id: "1",
                event_template_id: "126",
                event_template_version_id: "1"
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: "interaction",
                trigger_location_nm: "cms",
                trigger_source_nm: "cms_tooltip",
                trigger_object_nm: "no_trigger_object"
              },
              semantic: {
                semantic_event_nm: "cms_close"
              },
              cms_info: {
                block_id: ((_a = node == null ? void 0 : node.attribs) == null ? void 0 : _a.id) || "",
                block_type_cd: "tooltip",
                block_displayed_txt: children[0] || "",
                ...getCmsInfoForPage(pageProps)
              }
            });
          }, triggered: /* @__PURE__ */ jsx(Tooltip, { body: /* @__PURE__ */ jsx(Paragraph, { children }) }) }, `${nodeIndex}-tooltip-${node.name}`);
        };
        return /* @__PURE__ */ jsx(TooltipContainer, {});
      }
    },
    // Superscript
    {
      shouldProcessNode(node) {
        return node.name === "sup";
      },
      processNode(node, children, nodeIndex) {
        return /* @__PURE__ */ jsx(Box, { as: "sup", sx: {
          fontSize: "0.625em",
          lineHeight: 0
        }, children }, `${nodeIndex}-sup`);
      }
    },
    {
      // Anything else
      shouldProcessNode() {
        return true;
      },
      processNode: processNodeDefinitions.processDefaultNode
    }
  ];
}
function makeContentInteractive(content, options = {}) {
  return htmlToReactParser.parseWithInstructions(content, () => true, [...options.extraInstructions || [], ...processingInstructions({
    ...options
  })]);
}
export {
  makeContentInteractive as default
};
